import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { environment } from '../../../../environments/environment'
import { UserModel } from '../../../core/models/user.model'
import { FormGroup } from '@angular/forms'
import { ContactModel } from '../../../core/models/contact.model'
import { AffairContactModel } from '../../../core/models/affair-contact.model'
import { InterventionCalendarModel } from '../../../core/models/intervention-calendar.model'
import { DateTools } from '../../../core/tools/date-tools'
import { BehaviorSubject, Observable, tap } from 'rxjs'

interface requestParamsIntervetions {
  users?: number[]
  dateFrom?: string
  dateTo?: string
}
interface requestParamsGetClosest {
  users?: number[]
  dateFrom: string | null
  dateTo: string | null
  productsNames?: string[]
  number: number
  sortBy?: 'distance' | 'date' // Sort by distance (km) or date (earliest)
  affair: {
    workSiteLatitude: number
    workSiteLongitude: number
  }
  excludeInterventionId?: number | null // ID de l'intervention à exclure (pour la replanification)
}
interface responseParamssGetClosest {
  id: number
  name: string
  affair: {
    workSitePostalCode: string
    workSiteLocality: string
    workSiteLatitude: string
    workSiteLongitude: string
  }
  startDate: string
  usersTechnicians: UserModel[]
  distance: number
  duration_min?: number // Temps de trajet en minutes (si Valhalla)
  distance_type?: 'route' | 'haversine' // Type de distance calculée
  erpSource: string
}

/**
 * Intervention with slot indicators (combined response)
 */
export interface InterventionWithIndicators extends responseParamssGetClosest {
  slot_indicators?: {
    feasible_slots: number
    adjustable_slots: number
    total_slots: number
    slots: BatchSlotResult[]
    distance_to_affair_km: number | null
    duration_to_affair_min: number | null
    travel_from_home_min?: number | null
    start_location_type?: 'home' | 'hotel'
    end_location_type?: 'home' | 'hotel'
    start_location_lat?: number | null
    start_location_lon?: number | null
    end_location_lat?: number | null
    end_location_lon?: number | null
    start_location_city?: string | null
    end_location_city?: string | null
    // Journée d'hôtel sans coordonnées : trajet non calculé mais "à prendre en compte"
    start_travel_uncounted?: boolean
    end_travel_uncounted?: boolean
    calendar_events?: BatchCalendarEvent[]
  } | null
}

/**
 * Response for getClosestWithIndicators
 */
export interface ClosestWithIndicatorsResponse {
  status: string
  interventions: InterventionWithIndicators[]
  distances_calculated: number
  total_interventions: number
}

interface SelectsForCreateResponse {
  contacts: ContactModel[]
  affairContacts: AffairContactModel[]
  worksSupervisors: ContactModel[]
}

/**
 * Résultat du calcul batch pour une intervention
 */
export interface BatchSlotResult {
  start: string
  end: string
  available_minutes: number
  effective_available_minutes: number
  travel_from_previous_min: number | null
  travel_to_next_min: number | null
  travel_from_home_min: number | null
  travel_to_home_min: number | null
  // Journée d'hôtel sans coordonnées : trajet non calculé mais "à prendre en compte"
  start_travel_uncounted?: boolean
  end_travel_uncounted?: boolean
  home_departure_time: string | null
  home_return_time: string | null
  is_first_slot_of_day: boolean
  is_last_slot_of_day: boolean
  home_departure_is_tight: boolean
  home_return_is_late: boolean
  is_feasible: boolean
  needs_adjustment: boolean
  estimated_arrival: string
  latest_departure: string
  // do_not_shift flags for adjacent events
  previous_event_do_not_shift: boolean | null
  next_event_do_not_shift: boolean | null
  // Scenario insertion cost (Valhalla N×N)
  scenario_added_km: number | null
  scenario_total_km: number | null
  scenario_added_min: number | null
  // Shift-slot flags: true if this slot requires shifting an existing intervention
  requires_shift: boolean
  shifted_event_id: number | null
  shifted_event_title: string | null
}

export interface BatchCalendarEvent {
  title: string | null
  start: string
  end: string
  type: string // 'intervention' | 'event'
  doNotShift: boolean
  id: string | number | null
}

export interface BatchInterventionResult {
  intervention_id: number
  technician_id: number
  date: string
  feasible_slots: number
  adjustable_slots: number
  total_slots: number
  distance_to_affair_km: number | null
  duration_to_affair_min: number | null
  travel_from_home_min: number | null
  start_location_type?: 'home' | 'hotel'
  end_location_type?: 'home' | 'hotel'
  start_location_lat?: number | null
  start_location_lon?: number | null
  end_location_lat?: number | null
  end_location_lon?: number | null
  start_location_city?: string | null
  end_location_city?: string | null
  // Journée d'hôtel sans coordonnées : trajet non calculé mais "à prendre en compte"
  start_travel_uncounted?: boolean
  end_travel_uncounted?: boolean
  slots: BatchSlotResult[]
  calendar_events?: BatchCalendarEvent[]
}

@Injectable({
  providedIn: 'root',
})
export class InterventionsService {
  private http = inject(HttpClient)
  private planifyApiUrl = environment.planifyApiUrl

  // Reactive state for incoming interventions
  private _incomingInterventions = new BehaviorSubject<any[]>([])
  private _incomingTotal = new BehaviorSubject<number>(0)
  private _incomingLoading = new BehaviorSubject<boolean>(false)

  // Reactive state for interventions to reschedule
  private _toRescheduleInterventions = new BehaviorSubject<any[]>([])
  private _toRescheduleTotal = new BehaviorSubject<number>(0)
  private _toRescheduleLoading = new BehaviorSubject<boolean>(false)

  public incomingInterventions$ = this._incomingInterventions.asObservable()
  public incomingTotal$ = this._incomingTotal.asObservable()
  public incomingLoading$ = this._incomingLoading.asObservable()

  public toRescheduleInterventions$ =
    this._toRescheduleInterventions.asObservable()
  public toRescheduleTotal$ = this._toRescheduleTotal.asObservable()
  public toRescheduleLoading$ = this._toRescheduleLoading.asObservable()

  constructor() {}

  store(formData: any) {
    return this.http.post(`${this.planifyApiUrl}/interventions`, formData)
  }

  show(interventionId: number, erpSource: string) {
    return this.http.get<InterventionCalendarModel>(
      `${this.planifyApiUrl}/interventions/${interventionId}/show/${erpSource}`
    )
  }

  getInterventions(options: requestParamsIntervetions) {
    return this.http.post<InterventionCalendarModel[]>(
      `${this.planifyApiUrl}/interventions/get-for-calendar`,
      options
    )
  }

  getRoute(
    userId: number,
    interventions: any,
    startLocation?: { latitude: number; longitude: number } | null,
    endLocation?: { latitude: number; longitude: number } | null
  ) {
    return this.http.post<any>(`${this.planifyApiUrl}/interventions/route`, {
      userId: userId,
      interventions: interventions,
      startLocation: startLocation || undefined,
      endLocation: endLocation || undefined,
    })
  }

  getOptimizedRoute(userId: number, interventions: any) {
    return this.http.post<any>(
      `${this.planifyApiUrl}/interventions/optimized-route`,
      {
        userId: userId,
        interventions: interventions,
      }
    )
  }

  calculateDistances(userId: number, interventions: any) {
    return this.http.post<any>(
      `${this.planifyApiUrl}/interventions/calculate-distances`,
      {
        userId: userId,
        interventions: interventions,
      }
    )
  }

  /**
   * Calculate distances from a source point to multiple targets
   * @param source Source point with latitude and longitude
   * @param targets Array of target points with id, latitude, longitude
   */
  distancesFromSource(
    source: { latitude: number; longitude: number },
    targets: { id: string; latitude: number; longitude: number }[]
  ) {
    return this.http.post<{
      distances: { id: string; distance_km: number; duration_min: number }[]
      status: string
    }>(`${this.planifyApiUrl}/interventions/distances-from-source`, {
      source,
      targets,
    })
  }

  /**
   * Calcule les disponibilités avec les contraintes de trajet pour toutes les interventions
   * en un seul batch pour optimiser les performances
   */
  batchAvailabilitiesWithTravel(options: {
    affair: { workSiteLatitude: number; workSiteLongitude: number }
    interventions: {
      id: number
      technician_id: number
      start_date: string
      workSiteLatitude: number
      workSiteLongitude: number
    }[]
    start_date: string
    end_date: string
    duration: number
  }) {
    return this.http.post<{
      status: string
      interventions: BatchInterventionResult[]
      distances_calculated: number
    }>(
      `${this.planifyApiUrl}/interventions/batch-availabilities-with-travel`,
      options
    )
  }

  /**
   * Calcule les disponibilités avec trajets pour UNE intervention sélectionnée
   * Utilisé quand l'utilisateur clique sur une intervention pour voir les détails
   */
  singleAvailabilityWithTravel(options: {
    affair: { workSiteLatitude: number; workSiteLongitude: number }
    intervention: {
      id: number
      technician_id?: number
      startDate?: string
      start_date?: string
      workSiteLatitude?: number
      workSiteLongitude?: number
      usersTechnicians?: any[]
      data?: any
    }
    duration: number
  }) {
    return this.http.post<{
      status: string
      intervention_id: number
      technician_id: number
      date: string
      slots: BatchSlotResult[]
      feasible_slots: number
      adjustable_slots: number
      total_slots: number
      distance_to_affair_km: number | null
      duration_to_affair_min: number | null
      start_location_type?: 'home' | 'hotel'
      end_location_type?: 'home' | 'hotel'
      start_location_lat?: number | null
      start_location_lon?: number | null
      end_location_lat?: number | null
      end_location_lon?: number | null
      start_location_city?: string | null
      end_location_city?: string | null
      // Journée d'hôtel sans coordonnées : trajet non calculé mais "à prendre en compte"
      start_travel_uncounted?: boolean
      end_travel_uncounted?: boolean
    }>(
      `${this.planifyApiUrl}/interventions/single-availability-with-travel`,
      options
    )
  }

  /**
   * Calcule les indicateurs de créneaux pour toutes les interventions proches
   * Version optimisée: utilise les interventions pré-filtrées par getClosest
   * pour éviter de refaire la même requête EROS
   * @param closestInterventions Interventions déjà récupérées par getClosest (optionnel)
   * @param limit Limite optionnelle pour early exit
   */
  batchSlotIndicators(options: {
    affair: { workSiteLatitude: number; workSiteLongitude: number }
    date_from: string
    date_to: string
    duration: number
    products_names?: string[]
    limit?: number
    closest_interventions?: {
      id: number
      erpSource: string
      technician_email?: string // Email pour résoudre l'ID MyLink côté backend
      distance_km?: number
      duration_min?: number
      start_date?: string
      workSiteLatitude?: number
      workSiteLongitude?: number
    }[]
  }) {
    return this.http.post<{
      status: string
      interventions: BatchInterventionResult[]
      distances_calculated: number
      early_exit?: boolean
      total_with_slots?: number
    }>(`${this.planifyApiUrl}/interventions/batch-slot-indicators`, options)
  }

  getClosest(options: requestParamsGetClosest) {
    return this.http.post<responseParamssGetClosest[]>(
      `${this.planifyApiUrl}/interventions/closest`,
      options
    )
  }

  /**
   * Get closest interventions WITH slot indicators in a single API call
   * Optimizes performance by avoiding two separate HTTP calls
   * Combines getClosest + batchSlotIndicators into one request
   */
  getClosestWithIndicators(options: {
    affair: { workSiteLatitude: number; workSiteLongitude: number }
    dateFrom: string
    dateTo: string
    duration: number
    productsNames?: string[]
    number?: number
    sortBy?: 'distance' | 'date'
    excludeInterventionId?: number | null
  }) {
    return this.http.post<ClosestWithIndicatorsResponse>(
      `${this.planifyApiUrl}/interventions/closest-with-indicators`,
      options
    )
  }

  getIncoming(options: any) {
    this._incomingLoading.next(true)
    return this.http
      .post<any>(`${this.planifyApiUrl}/interventions/get-incoming`, options)
      .pipe(
        tap((response) => {
          // We no longer need to set the BehaviorSubjects as the component will handle it
          this._incomingLoading.next(false)
          return response
        })
      )
  }

  getToReschedule(options: any) {
    this._toRescheduleLoading.next(true)
    return this.http
      .post<any>(
        `${this.planifyApiUrl}/interventions/get-to-reschedule`,
        options
      )
      .pipe(
        tap((response) => {
          this._toRescheduleLoading.next(false)
          return response
        })
      )
  }

  getGeneratedName(formValue: any) {
    return this.http.post<string>(
      `${this.planifyApiUrl}/interventions/get-generated-name`,
      formValue
    )
  }

  getGeneratedDescription(formValue: any) {
    return this.http.post<string>(
      `${this.planifyApiUrl}/interventions/get-generated-description`,
      formValue
    )
  }

  getSelectsForCreate(formValue: any) {
    return this.http.post<SelectsForCreateResponse>(
      `${this.planifyApiUrl}/interventions/get-selects-for-create`,
      formValue
    )
  }

  confirm(interventionId: number, params: any) {
    return this.http.post(
      `${this.planifyApiUrl}/interventions/${interventionId}/confirm`,
      params
    )
  }

  markAsWaitingConfirmation(interventionId: number, erpSource: string) {
    return this.http.get(
      `${this.planifyApiUrl}/interventions/${interventionId}/mark-as-waiting-confirmation`,
      {
        params: { erpSource: erpSource },
      }
    )
  }

  updateDate(interventionId: number, params: any) {
    return this.http.put(
      `${this.planifyApiUrl}/interventions/${interventionId}/update-date`,
      params
    )
  }

  shift(interventionId: number, params: any) {
    const apiParams = { ...params }

    if (apiParams.startDateFormatted) {
      const d = apiParams.startDateFormatted
      apiParams.startDate = `${d.year}-${d.month
        .toString()
        .padStart(2, '0')}-${d.day.toString().padStart(2, '0')}T${d.hour
        .toString()
        .padStart(2, '0')}:${d.minute.toString().padStart(2, '0')}:${d.second
        .toString()
        .padStart(2, '0')}`

      delete apiParams.startDateFormatted
      delete apiParams.startDateOriginal
    } else if (apiParams.startDate instanceof Date) {
      const date = new Date(apiParams.startDate)
      apiParams.startDate = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}T${date
        .getHours()
        .toString()
        .padStart(
          2,
          '0'
        )}:${date.getMinutes().toString().padStart(2, '0')}:${date
        .getSeconds()
        .toString()
        .padStart(2, '0')}`
    }

    if (apiParams.durationString) {
      apiParams.duration = apiParams.durationString
      delete apiParams.durationString
    } else if (apiParams.duration instanceof Date) {
      apiParams.duration = DateTools.formatTimeOnly(apiParams.duration)
    }

    return this.http.post(
      `${this.planifyApiUrl}/interventions/${interventionId}/shift`,
      apiParams
    )
  }

  deprogram(interventionId: number, params: any) {
    return this.http.post(
      `${this.planifyApiUrl}/interventions/${interventionId}/deprogram`,
      params
    )
  }

  getShiftOrigins(erpSource: string) {
    return this.http.get(
      `${this.planifyApiUrl}/interventions/shift-origins/${erpSource}`
    )
  }

  calcTimeDifference(startDate: Date, endDate: Date): Date {
    const durationHours = Math.floor(
      Math.abs(startDate.getTime() - endDate.getTime()) / 3600000
    )
    const durationMinutes = Math.floor(
      (Math.abs(startDate.getTime() - endDate.getTime()) / 60000) % 60
    )
    const duration = new Date()
    duration.setHours(durationHours)
    duration.setMinutes(durationMinutes)
    duration.setSeconds(0)
    return duration
  }

  /**
   * Replanifie une intervention existante
   * @param data Données de replanification
   */
  reschedule(data: any) {
    return this.http.post(
      `${this.planifyApiUrl}/interventions/reschedule`,
      data
    )
  }

  findClosestInterventions(params: any): Observable<any[]> {
    return this.http.get<any[]>(`${this.planifyApiUrl}/interventions/closest`, {
      params: this.convertParamsForQueryString(params),
    })
  }

  // Méthode utilitaire pour convertir les paramètres d'objet en format compatible avec HttpParams
  private convertParamsForQueryString(params: any): any {
    const result: any = {}

    // Convertir les tableaux en chaînes séparées par des virgules
    for (const key in params) {
      if (params[key] !== null && params[key] !== undefined) {
        if (Array.isArray(params[key])) {
          result[key] = params[key].join(',')
        } else {
          result[key] = params[key]
        }
      }
    }

    return result
  }

  findOptimalSlots(options: {
    latitude: number
    longitude: number
    serviceDuration?: number
    maxRadiusKm?: number
    slotsCount?: number
  }) {
    return this.http.post<OptimalSlotsResponse>(
      `${this.planifyApiUrl}/tablot/optimal-slots`,
      options
    )
  }

  launchAffairOptimization(options?: {
    serviceDuration?: number
    maxRadiusKm?: number
  }) {
    return this.http.post<AffairOptimizationRunResponse>(
      `${this.planifyApiUrl}/affairs/optimization/launch`,
      options ?? {}
    )
  }

  getAffairOptimizationRun(runId: number) {
    return this.http.get<AffairOptimizationRunResponse>(
      `${this.planifyApiUrl}/affairs/optimization/runs/${runId}`
    )
  }

  getLatestAffairOptimization() {
    return this.http.get<AffairOptimizationRunResponse>(
      `${this.planifyApiUrl}/affairs/optimization/latest`
    )
  }

  getSlotGeometry(slotId: number) {
    return this.http.get<{ geometry: string | null }>(
      `${this.planifyApiUrl}/affairs/optimization/slots/${slotId}/geometry`
    )
  }

  // --- Affaires vivier manuelles (drafts créés depuis le Programmator) ---

  /**
   * Crée une affaire vivier manuelle (draft). Les créneaux arrivent ensuite
   * via le temps réel (affair_added, affair_optimizing, slots_recalculated).
   */
  createPoolAffair(payload: CreatePoolAffairPayload) {
    return this.http.post<CreatePoolAffairResponse>(
      `${this.planifyApiUrl}/affairs/optimization/pool`,
      payload
    )
  }

  /**
   * Ajoute une affaire EROS existante (mlRef) au vivier d'optimisation.
   * Même cycle de vie qu'un draft manuel : les créneaux arrivent via le
   * temps réel (affair_added, affair_optimizing, slots_recalculated).
   */
  addExistingAffairToPool(payload: AddExistingPoolAffairPayload) {
    return this.http.post<CreatePoolAffairResponse>(
      `${this.planifyApiUrl}/affairs/optimization/pool/existing`,
      payload
    )
  }

  /** Supprime un draft manuel du vivier (abandon). */
  deletePoolAffair(affairId: number) {
    return this.http.delete<{ removed: boolean }>(
      `${this.planifyApiUrl}/affairs/optimization/pool/${affairId}`
    )
  }

  /**
   * Finalise un draft manuel : crée l'affaire puis l'intervention dans EROS
   * (erpSource déduite du technicien du créneau) et retire le draft du vivier.
   */
  schedulePoolAffair(affairId: number, payload: SchedulePoolAffairPayload) {
    return this.http.post<SchedulePoolAffairResponse>(
      `${this.planifyApiUrl}/affairs/optimization/pool/${affairId}/schedule`,
      payload
    )
  }
}

// --- Affaires vivier manuelles (drafts) ---

export interface CreatePoolAffairPayload {
  customerLastname: string
  address: string
  postalCode: string
  locality: string
  latitude: number
  longitude: number
  affairProducts: { productId: number; quantity: number }[]
  /** Marché de l'affaire (défaut : Maison individuelle). */
  marketTypeId?: number | null
  wishedPlanningStartDate?: string | null
  wishedPlanningEndDate?: string | null
  planningDeadline?: string | null
}

export interface AddExistingPoolAffairPayload {
  mlRef: string
  /**
   * Chaque produit est identifié par productId (catalogue) OU par
   * erpProductId + erpSource (produit planifiable EROS — la table locale
   * affair_product ne contient que les produits en sous-traitance).
   * affairProductId (idMylink) / idAbm / idNrj complètent le rattachement ;
   * name/abbreviation/duration servent de repli sans équivalent catalogue.
   */
  affairProducts: {
    productId?: number | null
    erpProductId?: number | null
    erpSource?: string | null
    affairProductId?: number | null
    idAbm?: number | null
    idNrj?: number | null
    name?: string | null
    abbreviation?: string | null
    duration?: number | null
    quantity: number
  }[]
  /**
   * Coordonnées du chantier : optionnelles si le back les connaît déjà.
   * Erreur 422 « coordonnées introuvables » si ni le back ni le payload
   * n'en disposent.
   */
  latitude?: number | null
  longitude?: number | null
  wishedPlanningStartDate?: string | null
  wishedPlanningEndDate?: string | null
  planningDeadline?: string | null
}

export interface CreatePoolAffairResponse {
  affairId: number
  source: string
  added: boolean
  optimized?: boolean
  slotsCount?: number
  deferred?: boolean
  runId?: number | null
}

export interface SchedulePoolAffairPayload {
  technicianId: number
  agencyId: number
  /** Heure « métier » encodée en UTC (convention vivier), ex. 2026-07-22T08:30:00.000Z */
  startDate: string
  duration?: string | null
  isConfirmed?: boolean
  /** Plage horaire annoncée au client (« HH:mm »). */
  isTimeSlot?: boolean
  startTimeSlot?: string | null
  endTimeSlot?: string | null
  userAssistantId?: number | null
  description?: string | null
  customerFirstname?: string | null
  customerPhone?: string | null
  customerEmail?: string | null
}

export interface SchedulePoolAffairResponse {
  status: 'completed' | 'affair_created_intervention_failed'
  mlRef: string
  erpSource: string
  /** Id de l'affaire dans l'ERP cible (ouverture de la fiche EROS). */
  erosAffairId?: number
  message?: string
  intervention?: any
}

export interface TablotTourStep {
  type: 'start' | 'end' | 'job'
  arrival: string | null
  duration_min: number
  service_min: number
  waiting_min: number
  distance_km: number | null
  location: [number, number] | null
  job_type?: 'tablot_affair' | 'scheduled_intervention' | 'unknown'
  job_id?: number
  affair_id?: number
  names?: string
  address?: string
  postal_code?: string
  locality?: string
  spreadsheet?: string
  intervention_id?: number
  name?: string
  start_date?: string
  erp_source?: string
  group_name?: string | null
}

export interface TablotTour {
  user_id: number
  technician_name: string
  date: string
  vehicle_id: number
  cost: number
  duration_min: number
  service_min: number
  waiting_min: number
  distance_km: number | null
  geometry: string | null
  steps: TablotTourStep[]
}


export interface OptimalSlot {
  date: string
  arrival: string | null
  travel_duration_min: number
  service_duration_min: number
  waiting_min: number
  distance_km: number | null
  technician_id: number
  technician_name: string
}

export interface OptimalSlotsResponse {
  status: string
  message: string
  slots: OptimalSlot[]
  parameters: {
    latitude: number
    longitude: number
    service_duration_seconds: number
    radius_km: number
    technicians_count: number
    technicians: { id: number; name: string }[]
    scan_from: string
    scan_to: string
  }
}

// --- Batch optimization ---

export interface AffairOptimizationSlotData {
  id: number
  rank: number
  date: string
  arrivalTime: string | null
  technicianId: number | null
  technicianName: string | null
  technicianEmail: string | null
  travelDurationMin: number
  serviceDurationMin: number
  waitingMin: number
  distanceKm: number | null
  tourTotalDurationMin: number | null
  tourTotalDistanceKm: number | null
  tourStopsCount: number | null
  geometry: string | null
  tourData: any | null
}

export interface AffairOptimizationResult {
  affairId: number
  affairName: string
  groupingName?: string | null
  grouping_name?: string | null
  source?: string | null
  mlRef?: string | null
  /** Agence cliente locale (affaires existantes) : présélectionnée à la validation du créneau. */
  agencyId?: number | null
  tablotFicheUrl?: string | null
  latitude: number
  longitude: number
  postalCode: string | null
  locality: string | null
  /** Adresse complète (affaires vivier manuelles). */
  address?: string | null
  /** Nom du client final (affaires vivier manuelles). */
  customerLastname?: string | null
  planning_deadline?: string | null
  /** Fenêtre de planification souhaitée (bornes incluses), transmise par EROS. */
  wishedPlanningStartDate?: string | null
  wishedPlanningEndDate?: string | null
  last_call_reason_type?: string | null
  last_call_reason_comment?: string | null
  affairProducts?:
    | { product_id?: number; product_name?: string; [key: string]: any }[]
    | null
  slots: AffairOptimizationSlotData[]
  bestTourDurationMin: number | null
  averageTourDurationMin: number | null
  efficiencyRank: number
}

export interface AffairPoolItemWithoutSlots {
  affairId: number
  affairName: string
  source?: string | null
  mlRef?: string | null
  /** Agence cliente locale (affaires existantes) : présélectionnée à la validation du créneau. */
  agencyId?: number | null
  latitude: number
  longitude: number
  postalCode: string | null
  locality: string | null
  /** Adresse complète (affaires vivier manuelles). */
  address?: string | null
  /** Nom du client final (affaires vivier manuelles). */
  customerLastname?: string | null
  affairProducts?:
    | { product_id?: number; product_name?: string; [key: string]: any }[]
    | null
  last_call_reason_type?: string | null
  last_call_reason_comment?: string | null
}

export interface OptimizationTechnicianHome {
  technicianId: number
  name: string
  latitude: number
  longitude: number
}

export interface AffairOptimizationRunResponse {
  id?: number
  status: string
  message?: string
  dateFrom?: string | null
  dateTo?: string | null
  serviceDuration?: number
  maxRadiusKm?: number
  clustersCount?: number | null
  techniciansCount?: number | null
  affairsCount?: number | null
  affairsWithSlotsCount?: number | null
  summary?: any
  errorMessage?: string | null
  startedAt?: string | null
  completedAt?: string | null
  createdAt?: string
  tablotBaseUrl?: string | null
  affairs: AffairOptimizationResult[]
  poolAffairsWithoutSlots?: AffairPoolItemWithoutSlots[]
  technicianHomes?: OptimizationTechnicianHome[]
}
